import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Check, Zap, Star, Sparkles, CreditCard, Loader2, AlertCircle } from "lucide-react";
import { cn } from "lib/utils";
import { useSubscriptionPlans, useSubscribePlan } from "core";
import pricingPlanData from "./pricing-plan.data";
import { PlanCardGridSkeleton } from "skeleton/plans-card/PlanCardSkeleton";

interface PlanItem {
  name: string;
  price: string | number;
  unitValue: string;
  annually: string;
  chargesInfo: string;
  planDesc: string;
  includes: string[];
  subscriptionPlanId: number;
  planCode: string;
}

interface BrowsePlanProps {
  open: boolean;
  onClose: () => void;
  currentPlanInfo?: { subscriptionPlanId: number };
  onSubscribe: (plan: PlanItem) => void;
}

const PLAN_STYLES: Record<string, {
  accent: string;
  accentText: string;
  accentBtn: string;
  accentRing: string;
  icon: React.ElementType;
  badge: string | null;
  tagline: string;
}> = {
  Basic: {
    accent: "from-indigo-500 to-violet-600",
    accentText: "text-indigo-400",
    accentBtn: "from-indigo-500 to-violet-600 hover:from-indigo-600 hover:to-violet-700",
    accentRing: "ring-indigo-500/50",
    icon: Zap,
    badge: null,
    tagline: "Perfect for growing businesses",
  },
  Pro: {
    accent: "from-violet-500 to-purple-600",
    accentText: "text-violet-400",
    accentBtn: "from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700",
    accentRing: "ring-violet-500/50",
    icon: Star,
    badge: "Most Popular",
    tagline: "Ideal for startups and SMBs",
  },
  "Pay-As-You-Go": {
    accent: "from-emerald-500 to-teal-600",
    accentText: "text-emerald-400",
    accentBtn: "from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700",
    accentRing: "ring-emerald-500/50",
    icon: Sparkles,
    badge: "Flexible",
    tagline: "For businesses with recurring customers",
  },
};

const DEFAULT_STYLE = PLAN_STYLES["Basic"];

const BrowsePlan: React.FC<BrowsePlanProps> = ({
  open,
  onClose,
  currentPlanInfo,
  onSubscribe,
}) => {
  const [selected, setSelected] = useState<number | null>(null);
  const [_hovering, setHovering] = useState<number | null>(null);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successPlan, setSuccessPlan] = useState<PlanItem | null>(null);

  const { data: apiPlans, isLoading, isError } = useSubscriptionPlans();
  const { mutateAsync: subscribePlanMutation } = useSubscribePlan();

  /* Auto-close after showing the success screen */
  useEffect(() => {
    if (!successPlan) return;
    const t = window.setTimeout(() => {
      onSubscribe(successPlan);
      onClose();
      setSuccessPlan(null);
    }, 2800);
    return () => window.clearTimeout(t);
  }, [successPlan]);

  const plansList: any[] = useMemo(() => {
    try {
      if (!apiPlans || !Array.isArray(apiPlans)) return pricingPlanData;
      return pricingPlanData.map((plan: any) => {
        const planCodeKey = plan.name.toLowerCase().replace(/ /g, "-");
        const apiPlan = apiPlans.find(
          (p: any) => (p.code ?? "").toLowerCase() === planCodeKey
        );
        if (!apiPlan) return plan;
        return {
          ...plan,
          // Keep local price and unitValue — API values use different denominations
          planCode: apiPlan.code,
          subscriptionPlanId: Number(
            apiPlan.subscriptionPlanId ?? apiPlan.subscriptionId ?? apiPlan.id
          ),
        };
      });
    } catch {
      return pricingPlanData;
    }
  }, [apiPlans]);

  const handleSubscribe = async (plan: PlanItem, index: number) => {
    setErrorMsg(null);
    setLoadingIndex(index);
    try {
      await subscribePlanMutation({ subscriptionPlanCode: plan.planCode });
      setSuccessPlan(plan);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Failed to subscribe. Please try again.";
      setErrorMsg(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoadingIndex(null);
    }
  };

  if (!open) return null;

  /* ── Success overlay ── */
  if (successPlan) {
    const style = PLAN_STYLES[successPlan.name] ?? DEFAULT_STYLE;
    const Icon = style.icon;

    return createPortal(
      <div className="fixed inset-0 z-[9999] flex items-center justify-center px-5 py-6 bg-slate-950/30 backdrop-blur-[2px] animate-fade-in">
        <div className="relative w-full max-w-[300px] overflow-hidden rounded-[28px] bg-surface shadow-[0_18px_46px_rgba(15,23,42,0.13)] ring-1 ring-slate-200/80 dark:bg-[#101217] dark:ring-white/[0.08] animate-modal-in">

          {/* soft premium background */}
          <div className="pointer-events-none absolute inset-0">
            <div
              className={`absolute -top-20 left-1/2 h-36 w-36 -translate-x-1/2 rounded-full bg-gradient-to-br ${style.accent} opacity-[0.085] blur-3xl`}
            />
            <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent dark:via-white/10" />
          </div>

          <div className="relative px-7 pb-7 pt-8 text-center">
            {/* success icon */}
            <div className="relative mx-auto mb-5 flex h-[58px] w-[58px] items-center justify-center">
              <div
                className={`absolute inset-0 rounded-full bg-gradient-to-br ${style.accent} opacity-10`}
              />

              <div
                className={`relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br ${style.accent} shadow-[0_10px_24px_rgba(15,23,42,0.12)] ring-[6px] ring-white dark:ring-[#101217] animate-check-pop`}
              >
                <Check className="h-6 w-6 text-white" strokeWidth={3} />
              </div>

              <div className="absolute bottom-0 right-0 flex h-5 w-5 items-center justify-center rounded-full border border-white bg-surface shadow-sm dark:border-[#101217] dark:bg-[#171a21]">
                <Icon className={`h-2.5 w-2.5 ${style.accentText}`} />
              </div>
            </div>

            {/* content */}
            <div className="animate-content-rise">
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500">
                Subscription active
              </p>

              <h2 className="mt-2 text-[23px] font-black leading-tight tracking-[-0.04em] text-slate-950 dark:text-white">
                {successPlan.name}
              </h2>

              <p className="mx-auto mt-3 max-w-[220px] text-ui leading-5 text-slate-500 dark:text-slate-400">
                Your subscription was updated successfully.
              </p>
            </div>

            {/* confirmation */}
            <div className="mt-5 animate-content-rise [animation-delay:0.1s]">
              <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-emerald-200/80 bg-emerald-50/80 px-4 py-2 text-emerald-700 shadow-[0_1px_0_rgba(255,255,255,0.75)_inset] dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/80 dark:bg-white/10">
                  <Check className="h-3 w-3" strokeWidth={3} />
                </span>

                <span className="text-[12px] font-bold">
                  Ready to use
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-start lg:items-center justify-center lg:p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={loadingIndex !== null ? undefined : onClose} />

      {/* Modal — full viewport on mobile/tablet, centered card on desktop */}
      <div
        className="relative w-full h-[100dvh] lg:h-auto lg:max-w-5xl lg:max-h-[90vh]
          flex flex-col bg-surface dark:bg-dark-800
          lg:rounded-2xl shadow-2xl overflow-hidden
          border-0 lg:border lg:border-line dark:lg:border-white/[0.08] safe-padding-top"
      >

        {/* Top rainbow accent bar */}
        <div className="absolute top-0 inset-x-0 h-[3px] bg-gradient-to-r from-indigo-500 via-violet-500 to-emerald-500 z-10" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 lg:px-7 pt-5 lg:pt-7 pb-4 lg:pb-5 flex-shrink-0">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/30">
                <CreditCard className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-[20px] font-bold text-content dark:text-white tracking-tight">
                Choose Your Plan
              </h2>
            </div>
            <p className="text-[13px] text-content-muted dark:text-gray-400 pl-[42px]">
              Scale your email marketing with the right plan for your business
            </p>
          </div>
          <button aria-label="Close"
            onClick={onClose}
            className="p-2 rounded-xl text-content-subtle hover:text-content-secondary dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-100 dark:bg-white/[0.06] mx-4 lg:mx-7 flex-shrink-0" />

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 px-4 lg:px-7 py-5 lg:py-6">

          {/* Loading — skeleton plan cards */}
          {isLoading && <PlanCardGridSkeleton />}

          {/* Fetch error */}
          {isError && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-500 dark:text-red-400" />
              </div>
              <div>
                <p className="text-[14px] font-semibold text-gray-800 dark:text-gray-200 mb-0.5">Failed to load plans</p>
                <p className="text-[12px] text-content-subtle dark:text-gray-500">Please close and try again.</p>
              </div>
            </div>
          )}

          {/* Inline API error banner */}
          {errorMsg && (
            <div className="flex items-start gap-3 mb-5 p-4 rounded-2xl bg-red-50 dark:bg-[#1f1215] border border-red-200/80 dark:border-red-500/20 border-l-4 border-l-red-400 dark:border-l-red-500 animate-slide-in-up">
              {/* Icon tile */}
              <div className="shrink-0 w-8 h-8 rounded-xl bg-red-200/80 dark:bg-red-500/25 flex items-center justify-center mt-0.5">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              {/* Text */}
              <div className="flex-1 min-w-0 pt-0.5">
                <p className="text-ui font-semibold text-red-700 dark:text-red-300 leading-none mb-1">
                  Subscription failed
                </p>
                <p className="text-meta text-red-600/80 dark:text-red-400/80 leading-snug">
                  {errorMsg}
                </p>
              </div>
              {/* Dismiss */}
              <button
                onClick={() => setErrorMsg(null)}
                className="shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-200/60 dark:hover:bg-red-500/20 hover:text-red-600 dark:hover:text-red-300 transition-all mt-0.5"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Plans grid */}
          {!isLoading && !isError && plansList.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {plansList.map((item: any, index: number) => {
                const style = PLAN_STYLES[item.name] ?? DEFAULT_STYLE;
                const Icon = style.icon;
                const isActive = currentPlanInfo?.subscriptionPlanId === item.subscriptionPlanId;
                const isSelected = selected === index;

                return (
                  <div
                    key={item.name}
                    onMouseEnter={() => setHovering(index)}
                    onMouseLeave={() => setHovering(null)}
                    onClick={() => !isActive && setSelected(index)}
                    className={cn(
                      "relative flex flex-col rounded-2xl cursor-pointer transition-all duration-200 overflow-hidden",
                      isActive
                        ? "ring-2 ring-emerald-500/60 border border-transparent bg-surface dark:bg-dark-700"
                        : isSelected
                          ? cn("ring-2 border border-transparent bg-surface dark:bg-dark-700", style.accentRing)
                          : "border border-line dark:border-white/[0.10] bg-surface dark:bg-dark-700/60 hover:border-line-strong dark:hover:border-white/[0.18] hover:bg-surface dark:hover:bg-dark-700"
                    )}
                  >
                    {/* Top gradient bar */}
                    <div className={cn("h-[3px] bg-gradient-to-r flex-shrink-0", style.accent)} />

                    <div className="relative flex flex-col flex-1 p-5">

                      {/* Active badge */}
                      {isActive && (
                        <div className="absolute top-0 right-4 px-2.5 py-0.5 rounded-b-lg text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg">
                          Current Plan
                        </div>
                      )}

                      {/* Style badge */}
                      {!isActive && style.badge && (
                        <div className={cn(
                          "absolute top-0 right-4 px-2.5 py-0.5 rounded-b-lg text-[10px] font-bold uppercase tracking-wider text-white shadow-lg bg-gradient-to-r",
                          style.accent
                        )}>
                          {style.badge}
                        </div>
                      )}

                      {/* Icon + Name */}
                      <div className="flex items-center gap-2.5 mb-4 mt-2">
                        <div className={cn(
                          "w-9 h-9 rounded-xl bg-gradient-to-br flex items-center justify-center shadow-lg flex-shrink-0",
                          style.accent
                        )}>
                          <Icon className="text-white" style={{ width: 18, height: 18 }} />
                        </div>
                        <div>
                          <h3 className="text-ui-lg font-bold text-content dark:text-white leading-tight">
                            {item.name}
                          </h3>
                          <p className="text-[11px] text-content-muted dark:text-gray-400">
                            {style.tagline}
                          </p>
                        </div>
                      </div>

                      {/* Pricing */}
                      <div className="mb-1">
                        <div className="flex items-baseline gap-1">
                          <span className={cn(
                            "text-[30px] font-black bg-gradient-to-br bg-clip-text text-transparent leading-none",
                            style.accent
                          )}>
                            {item.price}
                          </span>
                          <span className="text-[13px] font-semibold text-content-muted dark:text-gray-400">
                            /{item.unitValue}
                          </span>
                        </div>
                        {item.annually && (
                          <p className="text-[11px] text-content-muted dark:text-gray-400 mt-0.5">{item.annually}</p>
                        )}
                      </div>

                      {/* Charges note */}
                      {item.chargesInfo && (
                        <p className={cn("text-[10px] font-semibold mb-4", style.accentText)}>
                          {item.chargesInfo}
                        </p>
                      )}

                      {/* Divider */}
                      <div className="h-px bg-gray-100 dark:bg-white/[0.07] mb-4" />

                      {/* Description */}
                      <p className="text-meta text-content-secondary dark:text-gray-300 mb-3 leading-relaxed">
                        {item.planDesc}
                      </p>

                      {/* Features */}
                      <ul className="space-y-2.5 flex-1">
                        {(item.includes ?? []).map((feature: string, i: number) => (
                          <li key={i} className="flex items-start gap-2">
                            <div className={cn(
                              "mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-br flex items-center justify-center",
                              style.accent
                            )}>
                              <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                            </div>
                            <span className="text-meta text-gray-700 dark:text-gray-200 leading-snug">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {/* CTA */}
                      <button
                        disabled={isActive || loadingIndex !== null}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSubscribe(item, index);
                        }}
                        className={cn(
                          "mt-5 w-full py-2.5 rounded-xl text-[13px] font-bold text-white shadow-lg transition-all duration-150 active:scale-[0.98] bg-gradient-to-r flex items-center justify-center gap-2",
                          isActive
                            ? "from-gray-400 to-gray-500 cursor-not-allowed opacity-60"
                            : loadingIndex !== null && loadingIndex !== index
                              ? "from-gray-400 to-gray-500 cursor-not-allowed opacity-50"
                              : style.accentBtn
                        )}
                      >
                        {loadingIndex === index ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Activating…
                          </>
                        ) : isActive ? "Current Plan" : "Get Started"}
                      </button>
                    </div>

                    {/* Selected check */}
                    {isSelected && !isActive && (
                      <div className={cn(
                        "absolute top-3.5 left-3.5 w-5 h-5 rounded-full bg-gradient-to-br flex items-center justify-center shadow-md",
                        style.accent
                      )}>
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default BrowsePlan;
